// ============================================================================
// Mesa de Servicio — «Revisión» del canal de grupos de WhatsApp (#728).
// ============================================================================
// Lo que las bandejas de entrada y salida NO se atreven a decidir solas y
// antes sólo se veía en los logs y en Firestore:
//   • eventos de WhatsApp que agotaron sus intentos o cuyo mensaje no se pudo
//     leer, y respuestas del bot que PUDIERON haber salido;
//   • avisos del bot que fallaron, que Meta nunca confirmó o que no se mandaron
//     porque cambió la configuración, y archivos del cliente que no se guardaron;
//   • ALERTAS de tráfico real descartado (la mesa en el simulador o el canal
//     apagado mientras los clientes escriben).
// Acciones: «Reprocesar», «Reenviar deliberadamente», «Marcar resuelto» y
// «Enterado». Todas piden confirmación (con nota opcional) y el backend las
// audita; al resolver, el documento empieza a caducar.
//
// El alcance por marca lo aplica el backend: esta lista ya viene filtrada.
// La lista va ordenada por fecha (lo más nuevo primero) y paginada (#730):
// «Cargar más» pide la siguiente página con el cursor que devolvió la anterior.
import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { getError } from "../../../utils/getError";
import { formatDate } from "../services/serviceDeskService";
import { SERVICE_DESK_BASE } from "../config";
import {
  listWhatsappReview,
  reprocessInboxReview,
  resolveInboxReview,
  resendOutboxReview,
  reprocessOutboxReview,
  resolveOutboxReview,
  acknowledgeDropAlert,
  reviewKindLabel,
  type WaReviewData,
  type WaReviewItem,
  type WaDropAlert,
  type WaAmbiguousChoice,
  type WaReviewActionResult,
} from "../services/whatsappGroupsService";

/**
 * Qué icono merece lo que contestó el servidor. Un 200 NO siempre es éxito:
 * el reenvío pudo volver a fallar o descartarse, y el reproceso pudo quedar
 * pendiente o descartado. Sólo es éxito si nada de eso pasó.
 */
export function outcomeIcon(result: WaReviewActionResult | null | undefined): "success" | "warning" | "error" {
  const r = result || {};
  const repliesDropped = typeof r.reason === "string" && r.reason.startsWith("replies_dropped:");
  if (
    r.status === "dropped" ||
    r.decision === "dropped" ||
    r.decision === "channel_disabled" ||
    repliesDropped ||
    r.status === "failed"
  ) {
    return "error";
  }
  if (r.status === "needs_review" || r.ok === false) return "warning";
  return "success";
}

const REPLY_STATE_LABEL: Record<string, string> = {
  pending: "pendiente",
  sending: "enviándose",
  sent: "entregada a la bandeja de salida",
  ambiguous: "EN DUDA: pudo haber salido",
  dropped: "descartada",
};

interface Props {
  /** Con el canal apagado, reprocesar o reenviar sólo volvería a descartar. */
  channelEnabled: boolean;
  /** Nombre legible de una compañía por id (la lista trae ids). */
  companyName?: (id: string) => string | null;
}

/** Pide confirmación con nota opcional. `null` = canceló. */
async function confirmWithNote(title: string, text: string, confirmButtonText: string): Promise<string | null> {
  const res = await Swal.fire({
    title,
    text,
    icon: "question",
    input: "textarea",
    inputPlaceholder: "Nota para la auditoría (opcional)",
    showCancelButton: true,
    cancelButtonText: "Cancelar",
    confirmButtonText,
  });
  if (!res || !res.isConfirmed) return null;
  return typeof res.value === "string" ? res.value : "";
}

export default function WhatsappReviewPanel({ channelEnabled, companyName }: Props) {
  const [data, setData] = useState<WaReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";
  const btn = "rounded border px-2 py-1 text-xs font-medium disabled:opacity-50";

  const load = useCallback(async () => {
    try {
      setData(await listWhatsappReview());
      setError(null);
    } catch (err) {
      setError(getError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Siguiente página (#730): se AÑADE a lo ya cargado; las alertas vienen sólo en la primera. */
  const loadMore = async () => {
    if (!data?.nextCursor) return;
    setLoadingMore(true);
    try {
      const next = await listWhatsappReview({ cursor: data.nextCursor });
      setData((prev) => {
        if (!prev) return next;
        const seen = new Set(prev.items.map((i) => `${i.source}-${i.id}`));
        return {
          ...prev,
          items: [...prev.items, ...next.items.filter((i) => !seen.has(`${i.source}-${i.id}`))],
          truncated: next.truncated,
          nextCursor: next.nextCursor,
        };
      });
      setError(null);
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const act = async (fn: () => Promise<WaReviewActionResult | unknown>, okTitle: string, errorTitle: string) => {
    setBusy(true);
    try {
      const result = ((await fn()) || {}) as WaReviewActionResult;
      const icon = outcomeIcon(result);
      // El título es lo que el servidor dice que pasó; `okTitle` sólo si no dijo nada.
      const title = result.message || okTitle;
      if (icon === "success") {
        Swal.fire({ icon, title, timer: 1800, showConfirmButton: false });
      } else {
        const detail = [result.status, result.decision, result.reason || result.dropReason || result.lastError]
          .filter(Boolean)
          .join(" · ");
        Swal.fire({ icon, title, text: detail ? `Estado: ${detail}` : undefined });
      }
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: errorTitle, text: getError(err) });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onReprocessInbox = async (item: WaReviewItem, ambiguous?: WaAmbiguousChoice) => {
    const texts: Record<string, [string, string, string]> = {
      resend: [
        "Reenviar deliberadamente",
        "La respuesta del bot PUDO haber llegado al grupo. Revisa el grupo antes: si ya llegó, el cliente la recibirá dos veces.",
        "Reenviar",
      ],
      skip: [
        "Reprocesar sin reenviar",
        "La respuesta en duda se da por entregada (o innecesaria) y no se vuelve a mandar. El resto del evento se procesa de nuevo.",
        "Reprocesar",
      ],
      none: [
        "Reprocesar el evento",
        "Se vuelve a procesar por el camino normal. No duplica tickets ni respuestas que ya salieron.",
        "Reprocesar",
      ],
    };
    const [title, body, cta] = texts[ambiguous || "none"];
    const note = await confirmWithNote(title, body, cta);
    if (note === null) return;
    await act(
      () => reprocessInboxReview(item.id, { ambiguous, note }),
      ambiguous === "resend" ? "Reenvío en curso" : "Evento reprocesado",
      "No se pudo reprocesar",
    );
  };

  const onResolve = async (item: WaReviewItem) => {
    const note = await confirmWithNote(
      "Marcar resuelto",
      "Confirma que ya lo atendiste (o que no hace falta). No se manda nada; el registro empieza a caducar.",
      "Marcar resuelto",
    );
    if (note === null) return;
    await act(
      () => (item.source === "inbox" ? resolveInboxReview(item.id, note) : resolveOutboxReview(item.id, note)),
      "Marcado como resuelto",
      "No se pudo marcar resuelto",
    );
  };

  const onResendOutbox = async (item: WaReviewItem) => {
    const note = await confirmWithNote(
      "Reenviar deliberadamente",
      "Este aviso PUDO haber llegado a parte del grupo. Revisa el grupo antes: si ya llegó, el cliente lo recibirá dos veces.",
      "Reenviar",
    );
    if (note === null) return;
    await act(() => resendOutboxReview(item.id, note), "Reenvío en curso", "No se pudo reenviar");
  };

  const onReprocessMedia = async (item: WaReviewItem) => {
    const isStatus = item.source === "outbox" && item.type === "delivery_status";
    const note = await confirmWithNote(
      isStatus ? "Reaplicar el estado de entrega" : "Reprocesar el archivo",
      isStatus
        ? "Se vuelve a aplicar al aviso el estado que mandó WhatsApp (enviado, entregado…). No se manda nada al grupo."
        : "Se vuelve a descargar de WhatsApp y a adjuntar al ticket. No duplica el adjunto.",
      "Reprocesar",
    );
    if (note === null) return;
    await act(
      () => reprocessOutboxReview(item.id, note),
      isStatus ? "Estado de entrega aplicado" : "Archivo reprocesado",
      isStatus ? "No se pudo reaplicar el estado" : "No se pudo reprocesar el archivo",
    );
  };

  const onAcknowledge = async (alert: WaDropAlert) => {
    const note = await confirmWithNote(
      "Enterado",
      "La alerta se cierra. Si vuelven a llegar mensajes reales mientras siga igual, se abre otra vez.",
      "Enterado",
    );
    if (note === null) return;
    await act(() => acknowledgeDropAlert(alert.id, note), "Alerta atendida", "No se pudo atender la alerta");
  };

  const disabledWhy = channelEnabled ? undefined : "El canal está apagado: actívalo para reprocesar o reenviar";
  const company = (id: string | null, fallback?: string | null) =>
    fallback || (id && companyName ? companyName(id) : null) || id || null;

  return (
    <section className={`rounded-xl border ${card}`} aria-labelledby="wa-review-title" data-testid="wa-review-panel">
      <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 border-line`}>
        <h2 id="wa-review-title" className={`text-sm font-semibold ${text}`}>
          Revisión: lo que espera a una persona
          {data ? ` (${data.items.length}${data.alerts.length ? ` · ${data.alerts.length} alerta(s)` : ""})` : ""}
        </h2>
        <button type="button" className={`${btn} border-gray-300`} disabled={busy} onClick={() => load()}>
          Actualizar
        </button>
      </div>

      <div className="space-y-3 p-4">
        {error && (
          <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900">
            No se pudo cargar la revisión: {error}
          </p>
        )}
        {!data && !error && <p className={`text-sm ${muted}`}>Cargando…</p>}

        {/* --- Alertas de tráfico real descartado -------------------------- */}
        {data?.alerts.map((a) => (
          <div
            key={a.id}
            role="alert"
            data-testid="wa-drop-alert"
            className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900"
          >
            <p>
              <strong>Se están perdiendo mensajes reales de clientes.</strong> {a.message}
            </p>
            <p className="mt-1">
              Número {a.phoneNumberId || "—"}
              {a.companyId ? ` · ${company(a.companyId)}` : ""} · descartados: {a.count}
              {a.lastAt ? ` · último: ${formatDate(a.lastAt)}` : ""}
            </p>
            <p className="mt-1">
              Para recibirlos, activa el canal y elige el conector oficial en Mesa de Servicio → Configuración → Canal
              de WhatsApp (grupos).
            </p>
            <button
              type="button"
              className={`${btn} mt-2 border-red-400 text-red-800`}
              disabled={busy}
              onClick={() => onAcknowledge(a)}
            >
              Enterado
            </button>
          </div>
        ))}

        {data && data.items.length === 0 && (
          <p className={`text-sm ${muted}`}>Nada atorado: todo lo que entró y salió por WhatsApp se procesó.</p>
        )}

        {/* --- Lo atorado en las bandejas ---------------------------------- */}
        {data && data.items.length > 0 && (
          <ul className="space-y-2">
            {data.items.map((item) => {
              const companyLabel = company(item.group?.companyId || item.companyId, item.group?.companyName);
              return (
                <li
                  key={`${item.source}-${item.id}`}
                  className={`rounded-lg border p-3 text-xs border-line`}
                  data-testid="wa-review-item"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">
                      {item.source === "inbox" ? "Entrada" : "Salida"}
                    </span>
                    <span className={`font-medium ${text}`}>{reviewKindLabel(item)}</span>
                    <span className={muted}>
                      {item.group?.subject || "Grupo desconocido"}
                      {companyLabel ? ` · ${companyLabel}` : " · sin compañía"}
                      {item.group && item.group.status !== "active" ? ` · grupo ${item.group.status}` : ""}
                    </span>
                  </div>
                  {item.reason && <p className={`mt-1 ${text}`}>Motivo: {item.reason}</p>}
                  {item.text && (
                    <p className={`mt-1 whitespace-pre-line ${muted}`}>
                      {item.source === "inbox" && item.authorMasked ? `${item.authorMasked}: ` : ""}“{item.text}”
                    </p>
                  )}
                  {item.source === "outbox" && item.mediaType && (
                    <p className={`mt-1 ${muted}`}>Archivo: {item.mediaType}</p>
                  )}
                  {item.source === "outbox" && item.type === "delivery_status" && (item.deliveryStatuses || []).length > 0 && (
                    <p className={`mt-1 ${muted}`}>Estados de WhatsApp sin aplicar: {(item.deliveryStatuses || []).join(", ")}</p>
                  )}
                  {item.source === "inbox" && item.replies.length > 0 && (
                    <ul className={`mt-1 space-y-0.5 ${muted}`}>
                      {item.replies.map((r) => (
                        <li key={r.index}>
                          Respuesta {r.index + 1} ({REPLY_STATE_LABEL[r.state || ""] || r.state}): {r.text || "—"}
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.ticketIds.length > 0 && (
                    <p className="mt-1">
                      {item.ticketIds.map((t) => (
                        <a
                          key={t}
                          className="mr-2 text-primary-600 underline"
                          href={`${SERVICE_DESK_BASE}/tickets/${encodeURIComponent(t)}`}
                        >
                          Ticket {t}
                        </a>
                      ))}
                    </p>
                  )}
                  <p className={`mt-1 ${muted}`}>
                    {item.source === "inbox"
                      ? `Intentos: ${item.attempts}${item.reviewAt ? ` · en revisión desde ${formatDate(item.reviewAt)}` : ""}`
                      : `Intentos: ${item.attempts}${item.failedAt ? ` · falló ${formatDate(item.failedAt)}` : ""}${
                          item.resendCount ? ` · reenviado ${item.resendCount} vez/veces` : ""
                        }`}
                  </p>
                  {item.source === "inbox" && item.history.length > 0 && (
                    <ul className={`mt-1 ${muted}`}>
                      {item.history.map((h, i) => (
                        <li key={`${h.action}-${i}`}>
                          {h.byName || h.by || "—"} · {h.action}
                          {h.at ? ` · ${formatDate(h.at)}` : ""}
                          {h.note ? ` · ${h.note}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.source === "inbox" && item.actions.reprocess && !item.actions.resendAmbiguous && (
                      <button
                        type="button"
                        className={`${btn} border-primary-500 text-primary-700`}
                        disabled={busy || !channelEnabled}
                        title={disabledWhy}
                        onClick={() => onReprocessInbox(item)}
                      >
                        Reprocesar
                      </button>
                    )}
                    {item.source === "inbox" && item.actions.resendAmbiguous && (
                      <>
                        <button
                          type="button"
                          className={`${btn} border-amber-500 text-amber-800`}
                          disabled={busy || !channelEnabled}
                          title={disabledWhy}
                          onClick={() => onReprocessInbox(item, "resend")}
                        >
                          Reenviar deliberadamente
                        </button>
                        <button
                          type="button"
                          className={`${btn} border-primary-500 text-primary-700`}
                          disabled={busy || !channelEnabled}
                          title={disabledWhy}
                          onClick={() => onReprocessInbox(item, "skip")}
                        >
                          Reprocesar sin reenviar
                        </button>
                      </>
                    )}
                    {item.source === "outbox" && item.actions.resend && (
                      <button
                        type="button"
                        className={`${btn} border-amber-500 text-amber-800`}
                        disabled={busy || !channelEnabled}
                        title={disabledWhy}
                        onClick={() => onResendOutbox(item)}
                      >
                        Reenviar deliberadamente
                      </button>
                    )}
                    {item.source === "outbox" && item.actions.reprocess && (
                      <button
                        type="button"
                        className={`${btn} border-primary-500 text-primary-700`}
                        disabled={busy || !channelEnabled}
                        title={disabledWhy}
                        onClick={() => onReprocessMedia(item)}
                      >
                        Reprocesar
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${btn} border-gray-300`}
                      disabled={busy}
                      onClick={() => onResolve(item)}
                    >
                      Marcar resuelto
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {data?.nextCursor && (
          <div className="flex justify-center">
            <button
              type="button"
              className={`${btn} border-gray-300`}
              disabled={busy || loadingMore}
              onClick={() => loadMore()}
              data-testid="wa-review-load-more"
            >
              {loadingMore ? "Cargando…" : "Cargar más"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
